import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router';
import { AxiosError } from 'axios';
import { verifyEmail } from '../lib/api';

type VerifyStatus = 'pending' | 'success' | 'error';

const parseVerificationError = (error: unknown): string => {
  const axiosError = error as AxiosError<{
    detail?: string;
    token?: string[];
    non_field_errors?: string[];
  }>;
  const payload = axiosError.response?.data;
  if (payload?.token?.length) return payload.token[0];
  if (payload?.non_field_errors?.length) return payload.non_field_errors[0];
  if (payload?.detail) return payload.detail;
  return 'Не удалось подтвердить email. Ссылка может быть просрочена или уже использована.';
};

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token')?.trim() || '', [searchParams]);
  const [status, setStatus] = useState<VerifyStatus>('pending');
  const [message, setMessage] = useState<string>('Подтверждаем email...');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage('В ссылке отсутствует токен подтверждения.');
        return;
      }

      try {
        const data = await verifyEmail(token);
        if (cancelled) return;
        setStatus('success');
        setMessage(data.detail || 'Email успешно подтвержден.');
      } catch (error) {
        if (cancelled) return;
        setStatus('error');
        setMessage(parseVerificationError(error));
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Подтверждение Email</title>
      </Helmet>
      <main className="min-h-screen bg-slate-50 px-4 py-10 text-slate-900">
        <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold">Подтверждение email</h1>
          <p className="mt-4 text-sm leading-6 text-slate-600">{message}</p>
          {status === 'pending' && (
            <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full w-1/2 animate-pulse rounded-full bg-sky-500" />
            </div>
          )}
        </div>
      </main>
    </>
  );
}
